import LABELS from "constants/labels.json";
import { ReactNode } from "react";
import { Paper } from "./api";
import NameParse, { ParsedNameType } from "./nameParser";

export interface ICiteParams {
  authors?: string[];
  author: string;
  title: string;
  journal: string;
  year: number;
  doi?: string;
  volume?: string;
  pages?: string;
  issue?: string;
}

export interface ICite {
  mla: string | ReactNode;
  apa: string | ReactNode;
  chicago: string | ReactNode;
  bibtex?: string | ReactNode;
  harvard: string | ReactNode;
  numeric?: string | ReactNode;
}

export type CitationType =
  | "mla"
  | "apa"
  | "chicago"
  | "bibtex"
  | "harvard"
  | "numeric"
  | "ris";

export type CitationTypeRisOmitted = Exclude<CitationType, "ris">;

interface IExtractCitationPage {
  firstPage: string;
  lastPage: string;
}

function getFirstnameInitials(parsed: ParsedNameType) {
  return parsed.firstName
    ? `${parsed.firstName[0]}.`
    : parsed.initials
      ? parsed.initials
      : "";
}

function formatCitationNameApa(names: string[]): string {
  let citationName = "";

  for (let i = 0; i < names.length; i++) {
    const parsed = NameParse.parse(names[i]);
    const firstName = getFirstnameInitials(parsed);
    const lastName = parsed.lastName;

    if (i === 0) {
      citationName += lastName + ", " + firstName;
    } else if (i === names.length - 1) {
      citationName += ", & " + lastName + ", " + firstName;
    } else {
      citationName += ", " + lastName + ", " + firstName;
    }
  }

  return citationName;
}

function formatCitationNameChicago(names: string[]): string {
  let citationName = "";

  for (let i = 0; i < names.length; i++) {
    if (i === 0) {
      citationName += names[i];
    } else if (i === names.length - 1) {
      citationName += " and " + names[i];
    } else {
      citationName += ", " + names[i];
    }
  }

  return citationName;
}

function formatCitationNameBibtex(names: string[]): string {
  let citationName = "";

  for (let i = 0; i < names.length; i++) {
    if (i === 0) {
      citationName += names[i];
    } else {
      citationName += " and " + names[i];
    }
  }

  return citationName;
}

export function getCite(params: ICiteParams): ICite {
  const parsedAuthorName = NameParse.parse(params.author);

  const firstInitial = getFirstnameInitials(parsedAuthorName);
  const lastName = parsedAuthorName.lastName;
  const splitTitle = params.title.split(" ");
  const firstWordTitle = splitTitle[0];
  const doi = params.doi || "";
  const doiUrl = doi ? `https://doi.org/${doi}` : "";
  const doiLink = doiUrl
    ? ` <a style='color: #4B778F; text-decoration: underline;' href='${doiUrl}' target='_blank'>${doiUrl}</a>.`
    : "";

  const apaAuthorName = params?.authors
    ? formatCitationNameApa(params.authors)
    : `${lastName}, ${firstInitial}`;
  const chicagoAuthorName = params?.authors
    ? formatCitationNameChicago(params.authors)
    : params.author;
  const bibtexAuthorName = params?.authors
    ? formatCitationNameBibtex(params.authors)
    : params.author;

  const mla = `${params.author} et al. "${params.title}." <em>${
    params.journal
  }</em>${
    params.volume
      ? `, ${params.volume}${params.issue ? `.${params.issue}` : ""}`
      : ""
  } (${params.year})${params.pages ? `: ${params.pages}` : ""}.${doiLink}`;

  const apa = `${apaAuthorName} (${params.year}). ${params.title}. <em>${
    params.journal
  }</em>${
    params.volume
      ? `, ${params.volume}${params.issue ? `(${params.issue})` : ""}`
      : ""
  }${params.pages ? `, ${params.pages}` : ""}.${doiLink}`;

  const chicago = `${chicagoAuthorName}. "${params.title}." <em>${
    params.journal
  }</em>${
    params.volume
      ? `, ${params.volume}${params.issue ? `, no. ${params.issue}` : ""}`
      : ""
  } (${params.year})${params.pages ? `: ${params.pages}` : ""}.${doiLink}`;

  const harvard = `${apaAuthorName}, ${params.year}. ${params.title}. <em>${
    params.journal
  }</em>${
    params.volume
      ? `, ${params.volume}${params.issue ? `(${params.issue})` : ""}`
      : ""
  }${params.pages ? `, pp. ${params.pages}` : ""}.${doiLink}`;

  const bibtex = `@article{${lastName}${
    params.year
  }${firstWordTitle},<br/>title={${
    params.title
  }},<br/>author={${bibtexAuthorName}},${
    params.journal ? `<br/>journal={${params.journal}},` : ""
  }<br/>year={${params.year}},${
    params.volume ? `<br/>volume={${params.volume}},` : ""
  }${params.issue ? `<br/>number={${params.issue}},` : ""}${
    params.pages ? `<br/>pages={${params.pages}},` : ""
  }<br/>doi={${doi}}<br/>}`;

  const numeric = `${apaAuthorName} ${params.title}. <em>${
    params.journal
  }.</em> ${params.year}${
    params.volume
      ? `; ${params.volume}${params.issue ? `(${params.issue})` : ""}.`
      : ""
  }${doiLink}`;

  return {
    mla,
    apa,
    chicago,
    bibtex,
    harvard,
    numeric,
  };
}

export function getMarkdownCite(params: ICiteParams): ICite {
  const parsedAuthorName = NameParse.parse(params.author);
  const firstInitial = getFirstnameInitials(parsedAuthorName);
  const lastName = parsedAuthorName.lastName;
  const doi = params.doi || "";
  const doiUrl = doi ? `https://doi.org/${doi}` : "";
  const doiLink = doiUrl || "";

  const apaAuthorName = params?.authors
    ? formatCitationNameApa(params.authors)
    : `${lastName}, ${firstInitial}`;
  const chicagoAuthorName = params?.authors
    ? formatCitationNameChicago(params.authors)
    : params.author;

  const mla = `${params.author} et al. "${params.title}." *${params.journal}*${
    params.volume
      ? `, ${params.volume}${params.issue ? `.${params.issue}` : ""}`
      : ""
  } (${params.year})${params.pages ? `: ${params.pages}` : ""}. ${doiLink}`;

  const apa = `${apaAuthorName} (${params.year}). ${params.title}. *${
    params.journal
  }*${
    params.volume
      ? `, ${params.volume}${params.issue ? `(${params.issue})` : ""}`
      : ""
  }${params.pages ? `, ${params.pages}` : ""}. ${doiLink}`;

  const chicago = `${chicagoAuthorName}. "${params.title}." *${
    params.journal
  }*${
    params.volume
      ? `, ${params.volume}${params.issue ? `, no. ${params.issue}` : ""}`
      : ""
  } (${params.year})${params.pages ? `: ${params.pages}` : ""}. ${doiLink}`;

  const harvard = `${apaAuthorName}, ${params.year}. ${params.title}. *${
    params.journal
  }*${
    params.volume
      ? `, ${params.volume}${params.issue ? `(${params.issue})` : ""}`
      : ""
  }${params.pages ? `, pp. ${params.pages}` : ""}. ${doiLink}`;

  const numeric = `${apaAuthorName} ${params.title}. *${params.journal}.* ${
    params.year
  }${
    params.volume
      ? `; ${params.volume}${params.issue ? `(${params.issue})` : ""}.`
      : ""
  } ${doiLink}`;

  return {
    mla,
    apa,
    chicago,
    harvard,
    numeric,
  };
}

/**
 * @function extractCitationPage
 * @description To get the firstPage and lastPage from the pages, the format should include a "-". For example, "10-12p".
 * @example
 * extractCitationPage("10-12p")
 */
export function extractCitationPage(pages?: string): IExtractCitationPage {
  let firstPage = "";
  let lastPage = "";
  if (pages?.includes("-")) {
    const splitPages = pages.split("-");
    if (splitPages.length > 1) {
      firstPage = splitPages[0].replace(/\D/g, "");
      lastPage = splitPages[1].replace(/\D/g, "");
    }
  }
  return {
    firstPage,
    lastPage,
  };
}

export const pruneCitations = (content: string) => {
  return content.replace(/(\[\d+\]|\{\{.*?\}\})/g, "");
};

export const getAuthorDisplayName = (fullname: string) => {
  const parsed = NameParse.parse(fullname);
  return parsed.lastName || parsed.firstName;
};

function formatInlineCitationNames(
  primaryAuthor: string,
  authors?: string[],
  citationType?: CitationType
): string {
  let citationName = "";
  if (!authors || !authors.length) {
    return citationName;
  }
  const firstAuthor = getAuthorDisplayName(primaryAuthor);
  let found = false;

  const otherAuthors = authors.filter((item) => {
    if (item === primaryAuthor && !found) {
      found = true;
      return false;
    }
    return true;
  });

  if (authors.length === 1) {
    return firstAuthor;
  }

  if (authors.length === 2) {
    const secondAuthor = getAuthorDisplayName(otherAuthors[0]);
    return (
      firstAuthor + ` ${citationType === "apa" ? "&" : "and"} ` + secondAuthor
    );
  }

  if (otherAuthors.length === 2 && citationType === "harvard") {
    return (
      firstAuthor +
      ", " +
      getAuthorDisplayName(otherAuthors[0]) +
      " and " +
      getAuthorDisplayName(otherAuthors[1])
    );
  }

  if (otherAuthors.length > 1) {
    return firstAuthor + ` et al.`;
  }

  return citationName;
}

export function getInlineCite(params: ICiteParams): ICite {
  const apa =
    formatInlineCitationNames(params.author, params.authors, "apa") +
    `, ${params.year}`;
  const mla =
    formatInlineCitationNames(params.author, params.authors) +
    `${params.pages ? ` ${params.pages}` : ""}`;
  const chicago =
    formatInlineCitationNames(params.author, params.authors) +
    ` ${params.year}` +
    `${params.pages ? `, ${params.pages}` : ""}`;
  const harvard =
    formatInlineCitationNames(params.author, params.authors, "harvard") +
    `, ${params.year}`;
  const bibtex = "";

  return {
    mla,
    apa,
    chicago,
    bibtex,
    harvard,
  };
}

export const getProAnalysisReferencesWithCite = (
  content: string,
  hits: Paper[]
) => {
  const matches = content.match(/(\[\d+\])/g);
  if (matches && hits && hits.length) {
    const citationNumbers = matches.map((cite) => {
      const citeMatch = cite.match(/^\[(\d+)\]$/);
      return Number(citeMatch?.[1]);
    });
    const uniqueCitations = Array.from([...new Set(citationNumbers)]).sort(
      (a: number, b: number): number => a - b
    );
    const citations = uniqueCitations.map((num: number) => {
      const hit = hits[num - 1];
      const cite = getMarkdownCite({
        authors: hit?.authors,
        author: hit?.primary_author ?? "",
        journal: hit?.journal ?? "",
        title: hit?.title ?? "",
        year: hit?.year ?? 0,
        doi: hit?.doi,
        volume: hit?.volume?.trim(),
        pages: hit?.pages?.trim(),
      });
      return { ...cite, citationNumber: num };
    });
    return citations;
  }
  return [];
};

export const getProAnalysisCitedContent = (
  proAnalysisContent: string,
  hits: Paper[],
  type: CitationTypeRisOmitted,
  appUrl: string,
  forCopyToText?: boolean
) => {
  const disclaimer = LABELS["pro-analysis"]["disclaimer"].replace(
    "(HOME_URL)",
    appUrl
  );
  const referencesTitle = LABELS["pro-analysis"]["references"];

  // Map of the original citation number to the new citation number in the order of appearance
  // If the first citation is [3], then the first entry in the map will be { [3]: 1 }
  // In the formatted content, the first citation will be updated to [1] and the third entry from
  // the paper hits will be listed first in the reference list below the formatted content.
  const numberCitationMap = new Map<string, number>();

  let content = proAnalysisContent.replace(
    /((?:\[\d+\]\s*)+)/g,
    (matchGroup) => {
      const cites = matchGroup.split(/(\[\d+\])/).map((part) => {
        const citationMatch = part.match(/^\[(\d+)\]$/);

        if (citationMatch) {
          const number = Number(citationMatch[1]);
          const paper: Paper | undefined = hits[number - 1];

          const cites = getInlineCite({
            authors: paper?.authors,
            author: paper?.primary_author ?? "",
            journal: "",
            title: paper?.title ?? "",
            year: paper?.year ?? 0,
            doi: paper?.doi,
            volume: paper?.volume,
            pages: paper?.pages,
          });
          if (type === "numeric") {
            /**
             * Re-numerate the text in the pro analysis (The first reference should be called "1" in the pro analysis text. The second paper it references should be "2", and so on)
             */
            const citation = citationMatch[0];
            if (!numberCitationMap.has(citation)) {
              // Set the citation number in the numberCitationMap to the current size of the map + 1
              // This is used to re-numerate the citations in the pro analysis content in the order of appearance
              numberCitationMap.set(citation, numberCitationMap.size + 1);
            }

            // Get the original citation number's position from the numberCitationMap
            const citeEntryPosition = numberCitationMap.get(citation);
            return {
              content: `[${citeEntryPosition}]`,
              doi: paper?.doi,
            };
          }
          return {
            content: cites[type],
            doi: paper?.doi,
          };
        } else {
          return { content: part };
        }
      });

      if (type === "numeric") {
        // Deduplicate consecutive identical citations for numeric format too
        const uniqueCites = cites
          .filter((item) => !!(item.content as string).trim())
          .map((cite) => cite.content);

        const deduplicated = uniqueCites.filter(
          (cite, index) => index === 0 || cite !== uniqueCites[index - 1]
        );

        return deduplicated.join(" ");
      } else {
        // Deduplicate consecutive identical citations
        const uniqueCites = cites
          .filter((item) => !!(item.content as string).trim())
          .map((cite) => cite.content);

        const deduplicated = uniqueCites.filter(
          (cite, index) => index === 0 || cite !== uniqueCites[index - 1]
        );

        return " (" + deduplicated.join("; ") + ")";
      }
    }
  );

  let referencesTxt = "";
  let references = getProAnalysisReferencesWithCite(proAnalysisContent, hits);
  if (type === "numeric") {
    const numericReferences: ICite[] = [];
    numberCitationMap.forEach((_, key) => {
      // Get the original citation number from the key by removing the surrounding square brackets
      const originalCitationNumber = Number(key.match(/^\[(\d+)\]$/)?.[1]);
      // Find the reference in the references array that has the same citation number
      const reference = references.find(
        (item) => item.citationNumber === originalCitationNumber
      );
      // Add the reference to the numericReferences array so it can be formatted in the reference list in its order of appearance
      // of the citations in the pro analysis content
      numericReferences.push(reference!);
    });
    // Format the numericReferences array into a string of references in the order of appearance of the citations in the pro analysis content
    referencesTxt = numericReferences.reduce((acc, item, index) => {
      return (
        acc +
        formatReferenceListNumberItem(index + 1, forCopyToText) +
        item[type] +
        "\n \n"
      );
    }, "");
  } else {
    referencesTxt = references.reduce((acc, item) => {
      return acc + item[type] + "\n \n";
    }, "");
  }

  const formattedCitedContent =
    content +
    "\n \n" +
    "_" +
    disclaimer +
    "_" +
    "\n \n" +
    "## " +
    referencesTitle +
    "\n \n" +
    referencesTxt;

  return formattedCitedContent;
};

const formatReferenceListNumberItem = (
  index: number,
  forCopyToText?: boolean
) => {
  return forCopyToText ? `\`${index}.\` ` : `${index}. `;
};
